import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';

import Box from '@mui/material/Box';
import Popper from '@mui/material/Popper';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import MuiDialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import InputBase from '@mui/material/InputBase';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';

import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import DoneIcon from '@mui/icons-material/Done';

import Draggable from 'react-draggable';

import StyledPaper from './styled/paper';
import IncidentDataService from '../data/incident-service.js';
import DataContext from '../data/data-context.js';

import useWindowSize from '../libraries/usewindowsize';

const useStyles = makeStyles(theme => ({
    root: {
        zIndex: theme.zIndex.modal + 3,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
    },
    paper: {
        maxWidth: 340,
        width: 340,
        '& .MuiListItemIcon-root': {
            minWidth: 0,
        },
    },
    tagsList: {
        maxHeight: 350,
        paddingRight: 0,
        paddingTop: 0,
        flexWrap: 'wrap',
        overflowY: 'scroll',
    },
    listitem: {
        height: 28,
        fontSize: 13,
        textTransform: 'uppercase',

        '&:hover': {
            fontWeight: 'bold'
        },

        '& svg': {
            marginRight: 5,
        },
    },
    info: {
        paddingLeft: 20,
        fontSize: 'smaller',
        paddingTop: 2,
        opacity: 0.6,
    },
    listEditText: {
        maxWidth: 150,
        whiteSpace: 'break-spaces',
        overflowWrap: 'break-word'
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500]
    },
    titlebar: {
        padding: 0,
        minHeight: 35,
    },
    saveButton: {
        margin: 0
    },
    iconButton: {
        margin: 4,
    }
}));

function PaperComponent (props) {
    return (
        <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <StyledPaper {...props} />
        </Draggable>
    );
}

const IncidentResponseList = (props) => {
    const classes = useStyles();
    const [state, setState] = useState('list');
    const [current, setCurrent] = useState();

    function handleListKeyDown (event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            props.onClickAway();
        }
    }

    const handleOnClick = i => {
        setCurrent(i);
        setState('incident');
        props.onStartSelection && props.onStartSelection(i.uuid);
    };

    return (
        <Popper
            className={classes.root}
            open={props.show}
            anchorEl={props.anchorEl}
            placement={props.placement || 'bottom-start'}
            role={undefined} disablePortal>
            <PaperComponent className={classes.paper}
                style={ (state === 'incident' && current) || state === 'list'
                    ? { maxWidth: 340, width: 340 } : {} }>
                <div
                    onKeyDown={handleListKeyDown}
                    style={{ cursor: 'move' }}
                    id="draggable-dialog-title">
                    {{
                        list: <Fade in={state === 'list'}>
                            <IncidentListList
                                onNew={() => setState('new')}
                                onClick={handleOnClick}
                                onClose={props.onClickAway}/>
                        </Fade>,
                        incident:
                            current && <IncidentListSelecting
                                incident={current}
                                onApply={() => { props.onFinishSelection(current); setState('list') }}
                                onClose={() => { props.onFinishSelection(); setState('list') }}/>,
                    }[state]}
                </div>
            </PaperComponent>
        </Popper>
    );
};

IncidentResponseList.propTypes = {
    show: PropTypes.bool.isRequired,
    placement: PropTypes.string,
    anchorEl: PropTypes.object,
    onClickAway: PropTypes.func.isRequired,
    onStartSelection: PropTypes.func,
    onFinishSelection: PropTypes.func,
    height: PropTypes.number,
};

const IncidentListList = React.forwardRef((props, ref) => {
    const classes = useStyles();
    const [search, setSearch] = useState('');
    const [incidents, setIncidents] = useState([]);
    const { mitreCategoriesMap } = useContext(DataContext);
    const { height } = useWindowSize();

    const handleSearchChange = event => {
        setSearch(event.target.value || '');
    };

    // tiny popup to fit a tiny monitor FML (1438 x 674)
    const getPapperStyle = useCallback(() => {
        return { height: Math.min(height / 3, 450) };
    }, [height]);

    // initial
    useEffect(() => {
        (async () => {
            IncidentDataService.getAll().then(res => setIncidents((res || [])
                .sort((a, b) => {
                    if (a.name.toUpperCase() < b.name.toUpperCase()) return -1;
                    return 1;
                })));
        })();
    }, []);

    return (
        <div ref={ref}>
            <MuiDialogTitle className={classes.titlebar}>
                {(incidents.length >= 11) && <React.Fragment>
                    <IconButton
                        className={classes.iconButton}
                        disabled={!search}
                        aria-label="search tags"
                        size="large">
                        <SearchIcon />
                    </IconButton>
                    <InputBase
                        className={classes.input}
                        onChange={handleSearchChange}
                        value={search}
                        placeholder={'Search incidents'}
                        inputProps={{ 'aria-label': 'Search incidents', spellCheck: false }}/>
                </React.Fragment>}
                <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={props.onClose}
                    size="large">
                    <CloseIcon fontSize="small"/>
                </IconButton>
            </MuiDialogTitle>
            <Box>
                <List className={classes.tagsList} style={getPapperStyle()}>
                    {incidents
                        .filter(inc => search.length < 0 || (inc.name || '').includes(search))
                        .map((inc, i) =>
                            <MenuItem
                                key={inc.name}
                                className={classes.listitem}
                                onClick={() => props.onClick(inc)}>
                                <Grid container
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    wrap='nowrap'>
                                    <Grid>{inc.name}</Grid>
                                    <Grid container
                                        justifyContent="flex-end"
                                        alignItems="center">
                                        <Grid className={classes.info}>
                                            {mitreCategoriesMap[inc.mitre_category_id]
                                                ? mitreCategoriesMap[inc.mitre_category_id].name
                                                : ''}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </MenuItem>
                        )}
                </List>
            </Box>
        </div>
    );
});
IncidentListList.displayName = 'IncidentListList';
IncidentListList.propTypes = {
    onNew: PropTypes.func,
    onClick: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

const IncidentListSelecting = (props) => {
    const classes = useStyles();

    return props.incident ? <Box>
        <ListItem
            size="small"
            button>
            <ListItemText className={classes.listEditText} primary={props.incident.name} />
            <React.Fragment>
                <ListItemSecondaryAction>
                    <IconButton
                        onClick={props.onApply}
                        edge="end"
                        color="primary"
                        className={classes.saveButton}
                        aria-label="save"
                        size="large">
                        <DoneIcon fontSize="small"/>
                    </IconButton>
                    <IconButton
                        onClick={props.onClose}
                        edge="end"
                        className={classes.saveButton}
                        aria-label="cancel"
                        size="large">
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </ListItemSecondaryAction>
            </React.Fragment>
        </ListItem>
    </Box> : '';
};
IncidentListSelecting.propTypes = {
    incident: PropTypes.object,
    onApply: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

export {
    IncidentResponseList as default, // for use in alert/mtdt list
};
